/**
 * One sentence a person can read, from any error the app can produce.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Error handling here was `e instanceof Error ? e.message : 'Something failed.'`, repeated
 * at every call site. That reads as careful — it has a fallback — and it hands the screen
 * whatever the layer below happened to put in `message`. Which is how a tenant
 * administrator came to be looking at this, in a red banner on the Users page:
 *
 *     { "message":"name resolution failed", "request_id":"cdd1544d2948be1178c7cd16465be23b" }
 *
 * The backend no longer emits that (see `lib/upstream-message.ts` there). This is the other
 * half: the browser must not render an arbitrary string just because something threw it,
 * because the next leak will come from somewhere nobody has thought of yet — a proxy, a
 * service worker, a browser extension, a fetch failure with a platform-specific message.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RULE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A message reaches the screen only if it reads like a sentence somebody wrote for a
 * person. Anything carrying structure, an identifier, an address or a stack frame is
 * replaced by a line chosen from the status. Nothing is ever partially redacted — a
 * half-scrubbed body is still a body.
 *
 * WHAT THIS IS NOT. It is not a way to hide failures. Every branch produces something the
 * reader can act on, and where an action exists it is named ("wait a moment and try again",
 * "sign in again"). Silence and "an error occurred" are both worse than a plain sentence.
 */
import { ApiError } from '@/lib/api/client';

/**
 * Does this read like prose written for a person?
 *
 * Mirrors the backend's check in `lib/upstream-message.ts` — deliberately, because the two
 * defend the same boundary from opposite sides and a reader comparing them should find the
 * same rules rather than two similar-looking sets.
 */
function looksLikeProse(text: string): boolean {
  if (text.length === 0 || text.length > 200) return false;
  if (/[{}[\]<>]/.test(text)) return false;
  if (/["`]/.test(text)) return false;
  if (/https?:\/\/|:\/\/|\bwww\./i.test(text)) return false;
  if (/[/\\]\w/.test(text)) return false;
  if (/\b\d{1,3}(\.\d{1,3}){3}\b/.test(text)) return false;
  if (/\b[\w-]+\.(com|net|org|io|ai|local|internal|svc|cluster)\b/i.test(text)) return false;
  if (/:\d{2,5}\b/.test(text)) return false;
  if (/\b[0-9a-f]{16,}\b/i.test(text)) return false;
  if (/\b[A-Za-z0-9_-]{24,}\b/.test(text)) return false;
  if (/\bat \w+\.|goroutine|panic:|Error:|Exception|TypeError|SyntaxError/i.test(text)) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(text)) return false;
  return /[a-z]{3}/i.test(text);
}

/** Infrastructure vocabulary — the same second line of defence the backend keeps. */
function mentionsInfrastructure(text: string): boolean {
  return /\b(dns|resolution|resolve|upstream|gateway|proxy|socket|dial|refused|unreachable|timed? ?out|timeout|connection|certificate|tls|ssl|handshake|cluster|container|hostname|node|pod|backend|cors|xhr|fetch)\b/i.test(
    text,
  );
}

/**
 * What to say when nothing survived, by status.
 *
 * Each names the next step where there is one. "Something went wrong" is not on this list:
 * it tells the reader nothing they did not already know from the fact that they are reading
 * an error.
 */
function byStatus(status: number): string {
  switch (status) {
    case 0:
      return 'Cannot reach the server. Check your connection and try again.';
    case 400:
      return 'That request was not valid. Check the details and try again.';
    case 401:
      return 'Your session has ended. Sign in again.';
    case 403:
      return 'Your role does not permit that.';
    case 404:
      return 'That no longer exists — it may have been removed.';
    case 409:
      return 'Somebody else changed this first. Reload and try again.';
    case 413:
      return 'That is too large to send.';
    case 422:
      return 'That change was refused. Check the values and try again.';
    case 429:
      return 'Too many requests. Wait a moment and try again.';
    case 502:
    case 503:
    case 504:
      return 'The service is not responding right now. Try again in a moment.';
    default:
      return status >= 500
        ? 'The server could not complete that. Try again in a moment.'
        : 'That did not work. Try again.';
  }
}

/**
 * The message for a person.
 *
 * @param err      anything a catch block received
 * @param fallback what to say when the error carries nothing usable and has no status —
 *                 name the action that failed, e.g. 'Could not save that binding.'
 */
export function humanError(err: unknown, fallback = 'That did not work. Try again.'): string {
  if (err instanceof ApiError) {
    /*
     * Field errors first: a 422 naming the field is the most actionable thing the API ever
     * returns, and it is written by us, so it does not go through the prose check.
     */
    const fields = Object.entries(err.fieldErrors);
    if (fields.length > 0) {
      const [path, message] = fields[0] as [string, string];
      return fields.length === 1 ? `${path}: ${message}` : `${path}: ${message} (+${fields.length - 1} more)`;
    }

    const detail = err.problem.detail?.trim() ?? '';
    if (detail !== '' && looksLikeProse(detail) && !mentionsInfrastructure(detail)) return detail;
    return byStatus(err.status);
  }

  /*
   * A TypeError from `fetch` is the offline case, and its message is platform-specific
   * ("Failed to fetch", "NetworkError when attempting to fetch resource", "Load failed").
   * None of those are worth showing; what the reader needs is that the network is the
   * problem, not their input.
   */
  if (err instanceof TypeError) return byStatus(0);

  /*
   * A REQUEST THAT RAN OUT OF TIME, which is a different thing from one that failed.
   *
   * `AbortSignal.timeout` rejects with a `TimeoutError` DOMException whose message is
   * "signal timed out" — prose, and infrastructure vocabulary, so it would otherwise land
   * on the generic fallback and tell the reader nothing about why they waited thirty
   * seconds. An explicit abort (a query unmounting) is `AbortError` and must stay silent:
   * nobody is waiting for that answer any more.
   */
  if (err instanceof DOMException) {
    if (err.name === 'TimeoutError') {
      return 'That took too long and was stopped. Your connection may be slow — try again.';
    }
    /*
     * Everything else from this family gets the caller's fallback, never its own message.
     * An `AbortError` carries the word "aborted", which passes the prose check — short,
     * lowercase, no structure — and is not in the infrastructure vocabulary, so it reached
     * the screen verbatim. A user reading "aborted" learns nothing and is shown an internal
     * word. Found by the test below rather than by inspection.
     */
    return fallback;
  }

  if (err instanceof Error) {
    const m = err.message.trim();
    if (m !== '' && looksLikeProse(m) && !mentionsInfrastructure(m)) return m;
    return fallback;
  }

  return fallback;
}
